import { APP_BUILD_VERSION } from "../constants/appBuildVersion";
import type { Booking } from "../types";
import type { BookingsUpdateApiResult } from "./mergeGlobalBookingsFromUpdate";

/** Cross-tab signal when BroadcastChannel is unavailable (legacy / private mode). */
export const LORENA_PORTAL_SYNC_KEY = "lorena_portal_sync";

const BC_NAME = "lorena_portal_v1";

export type PortalPeerPayload =
  | { v: 1; kind: "updateResult"; updateId: string; res: BookingsUpdateApiResult; clientMerged?: Booking | null }
  | { v: 1; kind: "upsertMany"; bookings: unknown[] }
  | { v: 1; kind: "remove"; id: string }
  | { v: 1; kind: "acceptRequest"; requestId: string; booking: unknown };

export type PortalPeerMessage = PortalPeerPayload & { tabId: string; buildVersion: string };

let tabIdCache: string | null = null;

export function getPortalTabId(): string {
  if (typeof window === "undefined") return "ssr";
  if (!tabIdCache) tabIdCache = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  return tabIdCache;
}

let channel: BroadcastChannel | null = null;

function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return null;
  if (!channel) {
    try {
      channel = new BroadcastChannel(BC_NAME);
    } catch {
      return null;
    }
  }
  return channel;
}

export function hasPortalBroadcastChannel(): boolean {
  return typeof window !== "undefined" && typeof BroadcastChannel !== "undefined";
}

export function pingPortalOtherTabs(): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(LORENA_PORTAL_SYNC_KEY, String(Date.now()));
  } catch {
    /* quota / private mode */
  }
}

/** Notify other Portal tabs with structured payload; falls back to storage tick if BC missing. */
export function postPortalPeerMessage(payload: PortalPeerPayload): void {
  const tabId = getPortalTabId();
  const body: PortalPeerMessage = { ...payload, tabId, buildVersion: APP_BUILD_VERSION };
  const ch = getBroadcastChannel();
  if (ch) {
    try {
      ch.postMessage(body);
    } catch {
      pingPortalOtherTabs();
    }
  } else {
    pingPortalOtherTabs();
  }
}

export function subscribePortalPeerSync(handler: (msg: PortalPeerMessage) => void): () => void {
  const ch = getBroadcastChannel();
  if (!ch) return () => {};
  const onMessage = (ev: MessageEvent) => {
    const d = ev.data;
    if (!d || d.v !== 1 || typeof d.tabId !== "string" || !d.kind) return;
    const buildVersion = typeof d.buildVersion === "string" ? d.buildVersion : "";
    handler({ ...d, buildVersion } as PortalPeerMessage);
  };
  ch.addEventListener("message", onMessage);
  return () => ch.removeEventListener("message", onMessage);
}

export { APP_BUILD_VERSION };
